import { addWeeks, startOfToday } from "date-fns";
import { Loader2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDateContext } from "@/components/providers/date-provider";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useAvailableTherapists } from "@/hooks/admin-portal/scheduling/use-available-therapists";
import { useTherapistCalendar } from "@/hooks/admin-portal/scheduling/use-therapist-calendar";
import {
	CONTINUITY_MODE,
	type ContinuityMode,
	DEFAULT_CONTINUITY_MODE,
	DEFAULT_EMPLOYMENT_TYPE,
	DEFAULT_MAX_VISIT_WEEKS,
	type EmploymentType,
} from "@/lib/therapist-scheduling/constants";
import { getCalendarModifiers } from "@/lib/therapist-scheduling/continuity";
import {
	formatIsoDate,
	formatSlotTime,
	toIsoDate,
} from "@/lib/therapist-scheduling/date";
import {
	employmentTypeToTherapistType,
	therapistTypeLabel,
} from "@/lib/therapist-scheduling/employment-type";
import type { SlotSelection } from "@/lib/therapist-scheduling/types";
import { cn } from "@/lib/utils";
import { EmploymentTypeFilter } from "./employment-type-filter";
import { ScheduleCalendar } from "./schedule-calendar";
import { TherapistAvailabilityList } from "./therapist-availability-list";

type ContinuityTherapist = {
	id: string;
	name?: string;
	/** When known, the type filter defaults to match this therapist (Visit 2++). */
	employmentType?: EmploymentType;
};

type SchedulePickerProps = {
	/** Booking location — drives the /available query. */
	latitude: number;
	longitude: number;
	locationId: string;
	serviceId: string;
	gender?: string;
	/** Optional selectable-date bounds. Omit for an unbounded picker (admin default). */
	minDate?: Date;
	maxDate?: Date;
	value?: SlotSelection | null;
	onChange?: (selection: SlotSelection | null) => void;
	/**
	 * Visit 2++ continuity anchor (resolved by the caller via
	 * `resolveContinuityTherapist`). When present and mode is ON, the calendar is
	 * coloured for this therapist and available dates show only them; unavailable
	 * dates fall back to the full list (§4.2).
	 */
	continuityTherapist?: ContinuityTherapist | null;
	defaultContinuityMode?: ContinuityMode;
	/**
	 * When true, picking a DIFFERENT therapist than the one currently committed
	 * for this visit opens a confirmation first (used when later visits are
	 * scheduled and would need to be reset). Cancel fully reverts the pick.
	 */
	shouldConfirmTherapistChange?: boolean;
	/** Called after the admin confirms a therapist change — reset subsequent visits here. */
	onTherapistChangeConfirmed?: () => void;
	/**
	 * "YYYY-MM-DD" dates already scheduled by OTHER visits in this booking. Struck
	 * through in the calendar so the admin sees a date is used elsewhere.
	 */
	scheduledDates?: string[];
	/**
	 * Side-by-side calendar + availability list from lg up, with the list scrolling
	 * in its own column. Off by default: create booking keeps the stacked layout.
	 */
	splitLayout?: boolean;
};

/** Same committed slot? (therapist + date + start time.) Both null ⇒ same. */
function sameSlotSelection(
	a: SlotSelection | null,
	b: SlotSelection | null,
): boolean {
	return (
		a?.therapistId === b?.therapistId &&
		a?.date === b?.date &&
		a?.start_time === b?.start_time
	);
}

/**
 * Which month the calendar should open on for a committed slot. A committed date
 * in the PAST (an existing order whose visit already happened) is dropped to
 * `undefined` so the calendar opens on the current month with actionable,
 * colour-coded dates — instead of a stale past month where every date is a
 * backdate and the continuity banner fires. The committed selection itself stays
 * shown in the card above; only the browse date resets.
 */
function browseDateFor(slot: SlotSelection | null): Date | undefined {
	if (!slot?.date) return undefined;
	const d = new Date(slot.date);
	return d < startOfToday() ? undefined : d;
}

/** Up-to-two-letter initials for the committed-slot avatar. */
function initials(name: string): string {
	return name
		.split(" ")
		.filter(Boolean)
		.slice(0, 2)
		.map((p) => p[0]?.toUpperCase() ?? "")
		.join("");
}

/**
 * Orchestrator. Visit 1 / show-all (§4.1) plus Visit 2++ continuity (§4.2).
 * Flow: employment-type filter → date → therapist + slot.
 */
export function SchedulePicker({
	latitude,
	longitude,
	locationId,
	serviceId,
	gender,
	minDate,
	maxDate,
	value,
	onChange,
	continuityTherapist,
	defaultContinuityMode,
	shouldConfirmTherapistChange,
	onTherapistChangeConfirmed,
	scheduledDates,
	splitLayout = false,
}: SchedulePickerProps) {
	const { locale, tzDate } = useDateContext();
	const { t } = useTranslation("appointments-form", {
		keyPrefix: "appt_schedule.schedule_picker",
	});
	// The type filter FOLLOWS the continuity therapist's type (Visit 2++) so an
	// external continuity therapist is actually returned by /available — unless
	// the admin has manually overridden it. Kept as derived state (not init-only)
	// so it reacts when the continuity therapist changes while already mounted.
	const [manualEmploymentType, setManualEmploymentType] =
		useState<EmploymentType | null>(null);
	const employmentType: EmploymentType =
		manualEmploymentType ??
		continuityTherapist?.employmentType ??
		DEFAULT_EMPLOYMENT_TYPE;
	const [mode, setMode] = useState<ContinuityMode>(
		defaultContinuityMode ?? DEFAULT_CONTINUITY_MODE,
	);
	const [date, setDate] = useState<Date | undefined>(() =>
		browseDateFor(value ?? null),
	);
	const [selection, setSelection] = useState<SlotSelection | null>(
		value ?? null,
	);

	// Selection is unbounded by default (admin picks any date, like the legacy
	// flow) — callers may still pass minDate/maxDate to constrain it. The
	// continuity calendar is fetched over a fixed colouring window only.
	const calendarStart = useMemo(() => startOfToday(), []);
	const calendarEnd = useMemo(
		() => addWeeks(calendarStart, DEFAULT_MAX_VISIT_WEEKS),
		[calendarStart],
	);

	const continuityActive = !!continuityTherapist && mode === CONTINUITY_MODE.ON;

	// Continuity calendar (only fetched when continuity is active).
	const { data: calendarData, isLoading: isCalendarLoading } =
		useTherapistCalendar(
			continuityActive ? (continuityTherapist?.id ?? null) : null,
			continuityActive ? toIsoDate(calendarStart) : null,
			continuityActive ? toIsoDate(calendarEnd) : null,
		);
	const calendarDays = calendarData?.data.calendar ?? [];
	const modifiers =
		continuityActive && calendarDays.length > 0
			? getCalendarModifiers(calendarDays)
			: undefined;

	const therapistType = employmentTypeToTherapistType(employmentType);
	// Only MALE/FEMALE are valid upstream; "NO PREFERENCE" (or anything else) must
	// be omitted entirely (§3: omit for "any").
	const genderParam =
		gender === "MALE" || gender === "FEMALE" ? gender : undefined;
	const { data, isLoading, error, refetch } = useAvailableTherapists({
		date: date ? toIsoDate(date) : undefined,
		latitude,
		longitude,
		location_id: locationId,
		service_id: serviceId,
		therapist_type: therapistType,
		gender: genderParam,
	});
	const therapists = data?.data.therapists ?? [];

	// Continuity display rule (§4.2): on an available date show only the
	// continuity therapist; on a date where they are genuinely busy fall back to
	// the full list with a banner. A date we have NO calendar data for (past, or
	// beyond the fetched window) is neither — show the full list WITHOUT the
	// banner, since we can't claim the therapist is unavailable.
	const continuityAvailableOnDate =
		continuityActive && date && modifiers ? modifiers.available(date) : false;
	const continuityBusyOnDate =
		continuityActive && date && modifiers ? modifiers.unavailable(date) : false;
	let displayTherapists = therapists;
	let showFallback = false;
	// Wait for the calendar to load before deciding — otherwise `modifiers` is
	// undefined mid-fetch and we'd flash a false "terapis tidak tersedia" banner.
	if (continuityActive && date && !isCalendarLoading) {
		if (continuityAvailableOnDate) {
			const only = therapists.filter((t) => t.id === continuityTherapist?.id);
			displayTherapists = only.length > 0 ? only : therapists;
		} else if (continuityBusyOnDate) {
			showFallback = true;
		}
	}

	// The last FULLY-COMMITTED selection for this visit — the baseline for
	// detecting a real therapist change and for reverting on cancel.
	const committedRef = useRef<SlotSelection | null>(value ?? null);
	// Guards the confirm dialog's onOpenChange so a button-driven close doesn't
	// double-fire the cancel path.
	const resolvingRef = useRef(false);
	const [pendingSelection, setPendingSelection] =
		useState<SlotSelection | null>(null);
	// Open when clearing a committed slot that has later scheduled visits — the
	// admin chooses whether those later visits are also reset.
	const [pendingClear, setPendingClear] = useState(false);

	// Adopt EXTERNAL `value` changes — e.g. a draft hydrating AFTER this picker
	// has mounted (the remount key only bumps on reset, not on hydration), so the
	// init-only state would otherwise stay empty. Our own commits set
	// committedRef to what we emit, so a value equal to it is an echo and is
	// skipped — no render loop, no fighting an in-progress pick.
	useEffect(() => {
		const incoming = value ?? null;
		if (sameSlotSelection(incoming, committedRef.current)) return;
		committedRef.current = incoming;
		setSelection(incoming);
		setDate(browseDateFor(incoming));
	}, [value]);

	// Fully apply a selection: update the UI, the committed baseline, and the form.
	const applySelection = (next: SlotSelection) => {
		setSelection(next);
		committedRef.current = next;
		onChange?.(next);
	};

	// Deliberately clear the committed slot. Propagates as a null (persisted).
	const doClear = () => {
		setSelection(null);
		committedRef.current = null;
		onChange?.(null);
	};

	// The explicit "Hapus pilihan". Clearing an earlier visit removes the
	// continuity anchor for the visits after it, so — when any are scheduled —
	// ask whether to also reset them (consistent with a therapist change) or keep
	// them. With no later visits, just clear.
	const handleClearSelection = () => {
		if (shouldConfirmTherapistChange && committedRef.current) {
			setPendingClear(true);
		} else {
			doClear();
		}
	};
	const clearKeepLater = () => {
		setPendingClear(false);
		doClear();
	};
	const clearAndResetLater = () => {
		setPendingClear(false);
		doClear();
		onTherapistChangeConfirmed?.();
	};

	const handleSelectSlot = (next: SlotSelection) => {
		const baselineTherapistId = committedRef.current?.therapistId ?? null;
		const isTherapistChange =
			!!shouldConfirmTherapistChange &&
			!!baselineTherapistId &&
			next.therapistId !== baselineTherapistId;
		if (isTherapistChange) {
			// Hold the pick (reflect it in the UI) but don't touch the form/baseline
			// until the admin confirms.
			resolvingRef.current = false;
			setSelection(next);
			setPendingSelection(next);
		} else {
			applySelection(next);
		}
	};

	const confirmChange = () => {
		resolvingRef.current = true;
		const next = pendingSelection;
		setPendingSelection(null);
		if (next) {
			applySelection(next);
			onTherapistChangeConfirmed?.();
		}
	};

	const cancelChange = () => {
		resolvingRef.current = true;
		setPendingSelection(null);
		// Revert the picker UI to the committed selection.
		const baseline = committedRef.current;
		setSelection(baseline);
		setDate(browseDateFor(baseline));
		onChange?.(baseline);
	};

	// Filter/date/mode changes now only affect which therapists are shown; they no
	// longer clear a committed slot. To change the slot the admin picks a different
	// one; to remove it entirely they use "Hapus pilihan".
	const handleEmploymentChange = (next: EmploymentType) => {
		setManualEmploymentType(next);
	};
	const handleDateChange = (next: Date | undefined) => {
		setDate(next);
	};
	const toggleMode = () => {
		setMode((m) =>
			m === CONTINUITY_MODE.ON ? CONTINUITY_MODE.OFF : CONTINUITY_MODE.ON,
		);
	};

	const continuityName =
		continuityTherapist?.name ?? t("continuity.default_name");

	// Highlight the committed slot only while browsing its own date — changing the
	// browse date no longer clears the commit, so the two can differ.
	const selectionOnThisDate =
		selection && date && toIsoDate(date) === selection.date ? selection : null;

	return (
		<div className="flex flex-col gap-4">
			<AlertDialog
				open={!!pendingSelection}
				onOpenChange={(open) => {
					if (!open && !resolvingRef.current) cancelChange();
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("change_dialog.title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("change_dialog.description")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={cancelChange}>
							{t("common.cancel")}
						</AlertDialogCancel>
						<AlertDialogAction onClick={confirmChange}>
							{t("common.continue")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog
				open={pendingClear}
				onOpenChange={(open) => {
					if (!open) setPendingClear(false);
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{t("clear_dialog.title")}</AlertDialogTitle>
						<AlertDialogDescription>
							{t("clear_dialog.description")}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
						<Button type="button" variant="outline" onClick={clearKeepLater}>
							{t("clear_dialog.keep_later")}
						</Button>
						<AlertDialogAction onClick={clearAndResetLater}>
							{t("clear_dialog.reset_later")}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{selection && (
				<div className="flex items-center gap-3 rounded-lg border border-border border-l-[3px] border-l-primary bg-card px-3 py-2.5 shadow-sm">
					<div className="grid size-10 shrink-0 place-items-center rounded-xl border border-border bg-accent font-semibold text-accent-foreground text-sm">
						{initials(selection.therapistName)}
					</div>
					<div className="min-w-0 flex-1">
						<p className="truncate font-bold text-sm uppercase tracking-wide">
							{selection.therapistName}
						</p>
						<p className="truncate text-muted-foreground text-xs">
							{formatIsoDate(selection.date, locale)} ·{" "}
							{formatSlotTime(selection.start_time, locale, tzDate)} ·{" "}
							{therapistTypeLabel(selection.therapistType)}
						</p>
					</div>
					<button
						type="button"
						onClick={handleClearSelection}
						className="shrink-0 font-medium text-primary text-xs hover:underline"
					>
						{t("common.change")}
					</button>
				</div>
			)}

			<EmploymentTypeFilter
				value={employmentType}
				onChange={handleEmploymentChange}
			/>

			{continuityTherapist && (
				// Inline caption (Option G): a muted one-liner above the grid, with a
				// link-style toggle — keeps the calendar itself the focus.
				<div className="flex flex-wrap items-center gap-x-2 gap-y-1 px-1 text-muted-foreground text-sm">
					{mode === CONTINUITY_MODE.ON ? (
						<span
							aria-hidden="true"
							className="flex h-3 w-5 shrink-0 overflow-hidden rounded-[3px]"
						>
							<span className="w-1/2 bg-emerald-500" />
							<span className="w-1/2 bg-orange-500" />
						</span>
					) : null}
					<span>
						{mode === CONTINUITY_MODE.ON
							? t("continuity.on_banner", { name: continuityName })
							: t("continuity.off_banner")}
					</span>
					<span aria-hidden="true" className="text-border">
						·
					</span>
					<button
						type="button"
						onClick={toggleMode}
						className="font-medium text-primary hover:underline"
					>
						{mode === CONTINUITY_MODE.ON
							? t("continuity.show_all")
							: t("continuity.use_continuity")}
					</button>
				</div>
			)}

			<div
				className={cn(
					"flex flex-col gap-4",
					splitLayout && "lg:flex-row lg:items-start",
				)}
			>
				<div className="relative w-fit shrink-0 rounded-lg border border-border">
					<ScheduleCalendar
						selected={date}
						onSelect={handleDateChange}
						minDate={minDate}
						maxDate={maxDate}
						modifiers={modifiers}
						showContinuityLegend={continuityActive}
						scheduledDates={scheduledDates}
					/>
					{/* The continuity calendar (/availability/calendar) drives the date
					    colouring; show a loading state while it fetches so the calendar
					    doesn't silently render uncoloured mid-request. */}
					{continuityActive && isCalendarLoading ? (
						<div className="absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-lg bg-background/70 text-sm text-muted-foreground backdrop-blur-[1px]">
							<Loader2 className="size-5 animate-spin" />
							<span>{t("calendar_loading")}</span>
						</div>
					) : null}
				</div>
				<div
					className={cn(
						"min-w-0",
						// The availability list already caps its own height and scrolls
						// internally — don't add a second scroll here (double scrollbar).
						splitLayout && "lg:flex-1",
					)}
				>
					{date ? (
						error ? (
							<div className="flex flex-col items-start gap-3 rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
								<p className="text-destructive">
									{t("error.load_failed", { message: error.message })}
								</p>
								<Button
									type="button"
									size="sm"
									variant="outline"
									onClick={() => refetch()}
								>
									{t("common.retry")}
								</Button>
							</div>
						) : (
							<div className="flex flex-col gap-3">
								{showFallback && (
									<div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
										{t("continuity.fallback_banner", { name: continuityName })}
									</div>
								)}
								<TherapistAvailabilityList
									therapists={displayTherapists}
									date={date}
									selection={selectionOnThisDate}
									onSelect={handleSelectSlot}
									isLoading={isLoading}
								/>
							</div>
						)
					) : (
						<div className="rounded-lg border border-border border-dashed p-6 text-center text-muted-foreground text-sm">
							{t("empty_date_prompt")}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
